import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Plus, Heart, MessageCircle, Bookmark, Share2 } from "lucide-react";
import { useAuth } from "../../global-components/src";
import { REFLEXO_API_URL, BUCKET_REFLEXO_URL } from "../../global-components/src/utils/vars";
import { getImageUser } from "../../global-components/src/utils/auth";
import ImgVerified from "../../components/imgVerified/ImgVerified";
import Spinner from "../../global-components/src/components/spinner/spiner";
import NavbarPC from "../../components/navbar-pc/navbar-pc";
import NavbarRight from "../../components/navbar-right/navbar-right";
import { getNativeStatusBarHeight, shouldHideMenus } from "../../utils/platform";
import ReelsFeed, { mediaUrl } from "./reels-feed";
import UploadReelModal from "./upload-reel-modal";
import { Reel } from "../../types";
import "./flow.css";

function GuestReelView({ reel }: { reel: Reel }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const goLogin = () => navigate("/login");

    return (
        <div className="reels-feed">
            <div className="reel-slot">
                <div className="reel-card">
                    <div className="reel-video-wrapper" onClick={goLogin}>
                        <video
                            src={mediaUrl(reel.video_path)}
                            poster={reel.thumbnail_path ? mediaUrl(reel.thumbnail_path) : undefined}
                            className="video-reels"
                            loop
                            muted
                            autoPlay
                            playsInline
                        />

                        <div className="reel-video-gradient" />

                        <div className="reel-video-footer">
                            <div className="reel-user-row">
                                <img
                                    src={getImageUser(reel.user.img_account ?? undefined)}
                                    alt={reel.user.user_name}
                                    className="reel-avatar"
                                />
                                <div className="reel-user-info">
                                    <span className="reel-username">
                                        @{reel.user.user_name}
                                        <ImgVerified classNameProp="verified-reel" user={reel.user} />
                                    </span>
                                    <span className="reel-name">{reel.user.name}</span>
                                </div>
                            </div>

                            {reel.caption && <p className="reel-caption">{reel.caption}</p>}
                        </div>
                    </div>

                    <div className="reel-actions">
                        <button className="reel-action-btn" onClick={goLogin} aria-label={t("flow.actions.like")}>
                            <span className="reel-action-icon">
                                <Heart size={26} />
                            </span>
                            <span className="reel-action-count">{reel.total_likes}</span>
                        </button>
                        <button className="reel-action-btn" onClick={goLogin} aria-label={t("flow.actions.comments")}>
                            <span className="reel-action-icon">
                                <MessageCircle size={26} />
                            </span>
                            <span className="reel-action-count">{reel.total_comments}</span>
                        </button>
                        <button className="reel-action-btn" onClick={goLogin} aria-label={t("flow.actions.save")}>
                            <span className="reel-action-icon">
                                <Bookmark size={24} />
                            </span>
                        </button>
                        <button className="reel-action-btn" onClick={goLogin} aria-label={t("flow.actions.share")}>
                            <span className="reel-action-icon">
                                <Share2 size={24} />
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Flow() {
    const { reelId } = useParams();
    const { token } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isAuthenticated = !!token;

    const [guestReel, setGuestReel] = useState<Reel | null>(null);
    const [guestLoading, setGuestLoading] = useState(!isAuthenticated && !!reelId);
    const [showUpload, setShowUpload] = useState(false);
    const [pendingReel, setPendingReel] = useState<Reel | null>(null);
    const statusBarHeight = getNativeStatusBarHeight() ?? 0;
    const hideMenus = shouldHideMenus();

    useEffect(() => {
        if (isAuthenticated || !reelId) return;
        setGuestLoading(true);
        axios
            .get(`${REFLEXO_API_URL}/public/reels/${reelId}`)
            .then(({ data }) => setGuestReel(data.reel ?? null))
            .catch(() => setGuestReel(null))
            .finally(() => setGuestLoading(false));
    }, [isAuthenticated, reelId]);

    return (
        <>
            <Helmet>
                {guestReel ? (
                    <>
                        <title>{`Paçoca - ${guestReel.user?.name} no Flow`}</title>
                        <meta
                            name="description"
                            content={guestReel.caption?.slice(0, 160) || `Veja o Flow de ${guestReel.user?.name} no Paçoca!`}
                        />
                        <meta property="og:title" content={`${guestReel.user?.name} - Flow - Paçoca`} />
                        <meta
                            property="og:description"
                            content={guestReel.caption?.slice(0, 160) || `Veja o Flow de ${guestReel.user?.name} no Paçoca!`}
                        />
                        <meta
                            property="og:image"
                            content={guestReel.thumbnail_path ? `${BUCKET_REFLEXO_URL}/${guestReel.thumbnail_path}` : "/img/pacoca-sem-braco-rounded.png"}
                        />
                        <meta property="og:url" content={window.location.href} />
                        <meta property="og:type" content="video.other" />
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:title" content={`${guestReel.user?.name} - Flow - Paçoca`} />
                        <meta
                            name="twitter:description"
                            content={guestReel.caption?.slice(0, 160) || `Veja o Flow de ${guestReel.user?.name} no Paçoca!`}
                        />
                        <meta
                            name="twitter:image"
                            content={guestReel.thumbnail_path ? `${BUCKET_REFLEXO_URL}/${guestReel.thumbnail_path}` : "/img/pacoca-sem-braco-rounded.png"}
                        />
                    </>
                ) : (
                    <title>Paçoca - Flow</title>
                )}
            </Helmet>

            {!hideMenus && (
                <div className="flow-desktop-navbar">
                    <NavbarPC />
                    <NavbarRight />
                </div>
            )}

            <div className="flow-screen">
                <header
                    className="flow-mobile-header"
                    style={{ "--flow-status-bar-height": `${statusBarHeight}px` } as React.CSSProperties}
                >
                    <button
                        type="button"
                        className="flow-mobile-header-button"
                        onClick={() => navigate(-1)}
                        aria-label={t("generics.back")}
                    >
                        <ArrowLeft size={25} />
                    </button>
                    <span className="flow-mobile-title">Flow</span>
                    {isAuthenticated ? (
                        <button
                            type="button"
                            className="flow-mobile-header-button"
                            onClick={() => setShowUpload(true)}
                            aria-label={t("flow.upload.title")}
                        >
                            <Plus size={27} />
                        </button>
                    ) : (
                        <span className="flow-mobile-header-placeholder" aria-hidden="true" />
                    )}
                </header>
                {isAuthenticated ? (
                    <>
                        <ReelsFeed
                            initialReelId={reelId ? Number(reelId) : undefined}
                            newReel={pendingReel}
                            onNewReelConsumed={() => setPendingReel(null)}
                        />

                        <button className="reel-upload-fab" type="button" onClick={() => setShowUpload(true)} aria-label={t("flow.upload.title")}>
                            <Plus size={26} />
                        </button>

                        {showUpload && (
                            <UploadReelModal
                                onClose={() => setShowUpload(false)}
                                onSuccess={(reel) => {
                                    setShowUpload(false);
                                    setPendingReel(reel);
                                }}
                            />
                        )}
                    </>
                ) : guestLoading ? (
                    <div className="reel-loading-slot">
                        <Spinner size="32" borderWidth="2.5px" className="reel-spinner" />
                    </div>
                ) : guestReel ? (
                    <GuestReelView reel={guestReel} />
                ) : (
                    <div className="reels-feed">
                        <div className="reel-slot reel-empty-slot">
                            <span className="span-empty">{t("generics.error")}</span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
